
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Calendar, Users, Clock, TrendingUp, DollarSign, BarChart3, Table2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useMonthlyReportData } from '@/hooks/useMonthlyReportData';
import { MonthlyReportTable } from './MonthlyReportTable';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const getMonthBounds = (date: Date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const toInputDate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const MonthlyReport: React.FC = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(() => getMonthBounds(new Date()));
  const [activeTab, setActiveTab] = useState<'charts' | 'table'>('charts');
  const { reportData, loading } = useMonthlyReportData(dateRange.start, dateRange.end);

  const goMonth = (offset: number) => {
    const d = new Date(dateRange.start);
    d.setMonth(d.getMonth() + offset);
    setDateRange(getMonthBounds(d));
  };

  const handleCustomStart = (val: string) => {
    const d = new Date(val + 'T00:00:00');
    if (!isNaN(d.getTime())) setDateRange(prev => ({ ...prev, start: d }));
  };

  const handleCustomEnd = (val: string) => {
    const d = new Date(val + 'T23:59:59');
    if (!isNaN(d.getTime())) setDateRange(prev => ({ ...prev, end: d }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-8">
            <div className="text-lg">Chargement du rapport...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate('/manager')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Rapport Mensuel</h1>
              <p className="text-muted-foreground">
                {reportData.monthName} {reportData.year}
              </p>
            </div>
          </div>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Télécharger PDF
          </Button>
        </div>

        {/* Date Selector + Tab Toggle */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => goMonth(-1)}>← Mois préc.</Button>
              <Button variant="outline" size="sm" onClick={() => setDateRange(getMonthBounds(new Date()))}>Ce mois</Button>
              <Button variant="outline" size="sm" onClick={() => goMonth(1)}>Mois suiv. →</Button>
              <div className="flex items-center gap-2 ml-auto">
                <label className="text-sm text-muted-foreground">Du</label>
                <input type="date" className="border rounded px-2 py-1 text-sm" value={toInputDate(dateRange.start)} onChange={e => handleCustomStart(e.target.value)} />
                <label className="text-sm text-muted-foreground">au</label>
                <input type="date" className="border rounded px-2 py-1 text-sm" value={toInputDate(dateRange.end)} onChange={e => handleCustomEnd(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-1">
              <Button variant={activeTab === 'charts' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('charts')}>
                <BarChart3 className="h-4 w-4 mr-1" /> Graphiques
              </Button>
              <Button variant={activeTab === 'table' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('table')}>
                <Table2 className="h-4 w-4 mr-1" /> Tableau
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Overview (always visible) */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <div className="text-2xl font-bold">{reportData.stats.activeEmployees}</div>
              <div className="text-sm text-muted-foreground">Employés actifs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <div className="text-2xl font-bold">{reportData.stats.totalHours}h</div>
              <div className="text-sm text-muted-foreground">Heures totales</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <DollarSign className="h-8 w-8 mx-auto mb-2 text-yellow-600" />
              <div className="text-2xl font-bold">{reportData.stats.revenue}$</div>
              <div className="text-sm text-muted-foreground">Revenus estimés</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <TrendingUp className="h-8 w-8 mx-auto mb-2 text-orange-600" />
              <div className="text-2xl font-bold">{reportData.stats.productivity}%</div>
              <div className="text-sm text-muted-foreground">Productivité</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="h-8 w-8 mx-auto mb-2 text-purple-600" />
              <div className="text-2xl font-bold">{reportData.stats.avgHoursPerDay}h</div>
              <div className="text-sm text-muted-foreground">Moyenne par jour</div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Content */}
        {activeTab === 'charts' ? (
          <>
            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Évolution hebdomadaire</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={reportData.weeklyTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="hours" stroke="#3b82f6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Répartition par projet</CardTitle></CardHeader>
                <CardContent>
                  {reportData.projectDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={reportData.projectDistribution} cx="50%" cy="50%" labelLine={false}
                          label={({ name, percentage }) => `${name} ${percentage}%`}
                          outerRadius={80} fill="#8884d8" dataKey="hours">
                          {reportData.projectDistribution.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted-foreground text-center py-12">Aucune donnée pour cette période</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Performance par employé</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={reportData.employeePerformance}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="hours" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Moyenne quotidienne</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={reportData.dailyAverage}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="hours" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Top Performers */}
            {reportData.topPerformers.length > 0 && (
              <Card>
                <CardHeader><CardTitle>Top Performers</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {reportData.topPerformers.map((performer, index) => (
                      <div key={index} className="text-center p-4 border rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">#{index + 1}</div>
                        <div className="font-semibold">{performer.name}</div>
                        <div className="text-sm text-muted-foreground">{performer.hours}h travaillées</div>
                        <div className="text-sm text-muted-foreground">{performer.projects} projets</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary */}
            <Card>
              <CardHeader><CardTitle>Résumé</CardTitle></CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="text-muted-foreground">
                    Sur cette période, l'équipe a enregistré {reportData.stats.totalHours} heures de travail
                    avec une productivité de {reportData.stats.productivity}%.
                    {reportData.stats.revenue > 0 && ` Revenu estimé : ${reportData.stats.revenue}$.`}
                    {reportData.stats.activeEmployees > 0 && ` ${reportData.stats.activeEmployees} employé(s) actif(s), moyenne de ${reportData.stats.avgHoursPerDay}h par jour.`}
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <MonthlyReportTable
            entries={reportData.rawEntries}
            profilesList={reportData.profilesList}
            projectsList={reportData.projectsList}
          />
        )}
      </div>
    </div>
  );
};
